import { RedoOutlined, SendOutlined } from "@ant-design/icons"
import { Alert, App, Button, Checkbox, Modal, Skeleton } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  getMessage,
  getRetryPreview,
  getSendNowPreview,
  retryMessage,
  sendMessageNow
} from "../api/messages.ts"
import { formatInZone } from "../composer/held-until.ts"
import HoldNotice from "../composer/HoldNotice.tsx"
import type {
  MessageDetail,
  QueueActionBlocker,
  QueueActionKind,
  QueueActionOutcome,
  RetryPreview,
  SendNowPreview
} from "../types/messages.types.ts"
import {
  OUTCOME_POLL_LIMIT,
  OUTCOME_POLL_MS,
  type QueueActionTarget,
  REFUSALS,
  STILL_GOING,
  canConfirm,
  offered,
  requestBody,
  settledTone,
  unwrapResult
} from "./queue-actions.ts"

type Preview = SendNowPreview | RetryPreview

const useZoneTime = () => {
  const { i18n } = useTranslation()
  const accountZone = useAppSelector(
    (state) => state.auth?.userInfo?.timezone as string | undefined
  )
  const zone =
    accountZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  return {
    accountZone: zone,
    at: (value: string | null | undefined) =>
      value ? formatInZone(value, zone, i18n.language || "en") : null
  }
}

const BlockerList = ({ blockers }: { blockers: QueueActionBlocker[] }) => {
  const { t } = useTranslation()
  const { at, accountZone } = useZoneTime()
  return (
    <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
      {blockers.map((blocker) => (
        <li key={blocker.code}>
          {t(`commHub.messages.actions.blocker.${blocker.code}`, {
            defaultValue: t(`communication.composer.reasons.${blocker.code}`, {
              defaultValue: blocker.code
            }),
            time: at(blocker.until) ?? "",
            zone: accountZone
          })}
        </li>
      ))}
    </ul>
  )
}

const Row = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <>
    <dt className="font-medium text-slate-500">{label}</dt>
    <dd className="m-0 text-slate-900">{children}</dd>
  </>
)

const SendNowBody = ({
  preview,
  target,
  confirmed,
  onConfirmedChange
}: {
  preview: SendNowPreview
  target: QueueActionTarget
  confirmed: boolean
  onConfirmedChange: (value: boolean) => void
}) => {
  const { t } = useTranslation()
  const { at, accountZone } = useZoneTime()
  const reason = preview.queue_reason || preview.hold_reason || "due"
  const timeRule = preview.hold_zone && preview.not_before
  const reasons = preview.override.reasons
    .map((code) => t(`commHub.messages.actions.overrideRule.${code}`))
    .join(t("commHub.messages.actions.and"))
  return (
    <div className="flex flex-col gap-4">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <Row label={t("commHub.messages.actions.recipient")}>
          {target.contact_name ? (
            <>
              {target.contact_name}{" "}
              <span className="text-slate-500">
                &lt;{preview.to_address}&gt;
              </span>
            </>
          ) : (
            preview.to_address
          )}
        </Row>
        <Row label={t("commHub.messages.actions.channel")}>
          {t(`commHub.channel.${preview.channel}`)}
        </Row>
        <Row label={t("commHub.messages.actions.waitingBecause")}>
          {t(`commHub.messages.queue.reason.${reason}`, {
            defaultValue: reason
          })}
        </Row>
        <Row label={t("commHub.messages.actions.plannedTime")}>
          {timeRule ? (
            <HoldNotice
              hold={preview}
              accountZone={accountZone}
              className="inline"
            />
          ) : preview.not_before ? (
            t("commHub.messages.actions.timeInZone", {
              time: at(preview.not_before),
              zone: accountZone
            })
          ) : (
            t("commHub.messages.queue.nextNow")
          )}
        </Row>
      </dl>
      {preview.scheduled_for && (
        <Alert
          type="info"
          showIcon
          message={t("commHub.messages.actions.earlierThanPlanned", {
            time: at(preview.scheduled_for),
            zone: accountZone
          })}
        />
      )}
      {preview.blockers.length > 0 ? (
        <Alert
          type="error"
          showIcon
          message={t("commHub.messages.actions.blockedTitle")}
          description={<BlockerList blockers={preview.blockers} />}
        />
      ) : (
        preview.override.needed && (
          <Alert
            type="warning"
            showIcon
            message={t("commHub.messages.actions.overrideTitle", {
              rules: reasons
            })}
            description={
              <div className="flex flex-col gap-2">
                <span>{t("commHub.messages.actions.overrideBody")}</span>
                <Checkbox
                  checked={confirmed}
                  onChange={(event) => onConfirmedChange(event.target.checked)}
                >
                  {t("commHub.messages.actions.overrideConfirm", {
                    rules: reasons
                  })}
                </Checkbox>
              </div>
            }
          />
        )
      )}
    </div>
  )
}

const RetryBody = ({
  preview,
  target,
  confirmed,
  onConfirmedChange
}: {
  preview: RetryPreview
  target: QueueActionTarget
  confirmed: boolean
  onConfirmedChange: (value: boolean) => void
}) => {
  const { t } = useTranslation()
  const { at, accountZone } = useZoneTime()
  const channel = t(`commHub.channel.${preview.channel}`)
  return (
    <div className="flex flex-col gap-4">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <Row label={t("commHub.messages.actions.recipient")}>
          {target.contact_name ? (
            <>
              {target.contact_name}{" "}
              <span className="text-slate-500">
                &lt;{preview.to_address}&gt;
              </span>
            </>
          ) : (
            preview.to_address
          )}
        </Row>
        <Row label={t("commHub.messages.actions.channel")}>{channel}</Row>
        <Row label={t("commHub.messages.actions.failureReason")}>
          {preview.failure_reason ||
            t("commHub.messages.actions.noFailureReason")}
        </Row>
        {preview.attempt_count > 0 && (
          <Row label={t("commHub.messages.actions.attempts")}>
            {preview.attempt_count}
          </Row>
        )}
      </dl>
      {!preview.not_retryable_reason && (
        <p className="m-0 text-sm text-slate-600">
          {t("commHub.messages.actions.onlyThisChannel", { channel })}
        </p>
      )}
      {preview.not_retryable_reason ? (
        <Alert
          type="info"
          showIcon
          message={t(
            `commHub.messages.actions.notRetryable.${preview.not_retryable_reason}`
          )}
        />
      ) : preview.blockers.length > 0 ? (
        <Alert
          type="error"
          showIcon
          message={t("commHub.messages.actions.blockedTitle")}
          description={<BlockerList blockers={preview.blockers} />}
        />
      ) : (
        <>
          {preview.uncertain && (
            <Alert
              type="warning"
              showIcon
              message={t("commHub.messages.actions.uncertainTitle")}
              description={
                <div className="flex flex-col gap-2">
                  <span>{t("commHub.messages.actions.uncertainBody")}</span>
                  <Checkbox
                    checked={confirmed}
                    onChange={(event) =>
                      onConfirmedChange(event.target.checked)
                    }
                  >
                    {t("commHub.messages.actions.uncertainConfirm")}
                  </Checkbox>
                </div>
              }
            />
          )}
          {preview.waits && (
            <Alert
              type="info"
              showIcon
              message={t("commHub.messages.actions.retryWaits", {
                reason: t(
                  `commHub.messages.queue.reason.${preview.waits.hold_reason}`,
                  { defaultValue: preview.waits.hold_reason }
                ),
                time: at(preview.waits.until),
                zone: accountZone
              })}
            />
          )}
        </>
      )}
    </div>
  )
}

export interface UseQueueActionOptions {
  target: QueueActionTarget
  /** Called with the message as it now is: after the request, and as its delivery settles. */
  onDone?: (message: MessageDetail) => void
}

/**
 * Send now and Retry for one message: the confirmation, the request, and
 * following the message until the worker has answered. One request at a time;
 * a second press while one is out does nothing.
 */
/**
 * A host's answer to Send now / Retry, held only while the host is on screen.
 * The follow-up outlives the button that started it, so it may report long
 * after the page that offered the action was left; reloading that page then
 * would read a list nobody is looking at.
 */
export const useWhileMounted = <A extends unknown[]>(
  callback: (...args: A) => void
) => {
  const alive = useRef(true)
  const latest = useRef(callback)
  latest.current = callback
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  return useCallback((...args: A) => {
    if (alive.current) latest.current(...args)
  }, [])
}

export const useQueueAction = ({ target, onDone }: UseQueueActionOptions) => {
  const { t } = useTranslation()
  const { message: toast, notification } = App.useApp()
  const [action, setAction] = useState<QueueActionKind | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [refusal, setRefusal] = useState<QueueActionOutcome | null>(null)
  const [failed, setFailed] = useState(false)
  const inFlight = useRef(false)
  const following = useRef<number | null>(null)
  const mounted = useRef(true)
  const lastSeen = useRef("")
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  // The follow-up outlives the button: once a message changes state the row
  // or pane that offered the action re-renders without it, and the person
  // must still hear what became of the message.
  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  const recipient = target.contact_name || target.to_address

  const announce = useCallback(
    (settled: MessageDetail) => {
      const tone = settledTone(settled)
      const text = t(`commHub.messages.actions.settled.${settled.status}`, {
        defaultValue: t("commHub.messages.actions.settled.other"),
        name: recipient,
        reason: settled.failure_reason || settled.status_reason || ""
      })
      if (tone === "error") notification.error({ message: text, duration: 0 })
      else if (tone === "warning")
        notification.warning({ message: text, duration: 0 })
      else if (tone === "success") toast.success(text)
      else toast.info(text)
    },
    [notification, recipient, t, toast]
  )

  const follow = useCallback(
    (id: string, attempt = 0) => {
      following.current = window.setTimeout(async () => {
        const response = await getMessage(id)
        if (!response.ok || !response.data) return
        const seen = `${response.data.status}:${response.data.hold_reason}`
        if (seen !== lastSeen.current) {
          lastSeen.current = seen
          onDoneRef.current?.(response.data)
        }
        if (
          !STILL_GOING.has(response.data.status) ||
          response.data.hold_reason
        ) {
          announce(response.data)
          return
        }
        if (attempt + 1 < OUTCOME_POLL_LIMIT) follow(id, attempt + 1)
      }, OUTCOME_POLL_MS)
    },
    [announce]
  )

  const loadPreview = useCallback(
    async (kind: QueueActionKind) => {
      setPreview(null)
      setLoadFailed(false)
      const response =
        kind === "send_now"
          ? await getSendNowPreview(target.id)
          : await getRetryPreview(target.id)
      if (!mounted.current) return
      if (response.ok && response.data) setPreview(response.data)
      else setLoadFailed(true)
    },
    [target.id]
  )

  const start = useCallback(
    (kind: QueueActionKind) => {
      setAction(kind)
      setConfirmed(false)
      setRefusal(null)
      setFailed(false)
      void loadPreview(kind)
    },
    [loadPreview]
  )

  const close = useCallback(() => {
    if (inFlight.current) return
    setAction(null)
    setPreview(null)
  }, [])

  const confirm = useCallback(async () => {
    if (!action || inFlight.current) return
    inFlight.current = true
    setSubmitting(true)
    setFailed(false)
    const body = requestBody(action, confirmed)
    const response =
      action === "send_now"
        ? await sendMessageNow(
            target.id,
            body as { confirm_outside_hours: boolean }
          )
        : await retryMessage(
            target.id,
            body as { confirm_not_received: boolean }
          )
    inFlight.current = false
    const shown = mounted.current
    if (shown) setSubmitting(false)
    const result = unwrapResult(response.data)
    if (!result) {
      if (shown) setFailed(true)
      return
    }
    lastSeen.current = `${result.message.status}:${result.message.hold_reason}`
    onDoneRef.current?.(result.message)
    if (REFUSALS.has(result.outcome)) {
      if (shown) {
        setRefusal(result.outcome)
        if (result.preview) setPreview(result.preview)
        setConfirmed(false)
      }
      return
    }
    if (shown) {
      setAction(null)
      setPreview(null)
    }
    const text = t(`commHub.messages.actions.outcome.${result.outcome}`, {
      name: recipient
    })
    if (result.outcome === "processing") {
      toast.loading({ content: text, duration: 2 })
      follow(result.message.id)
    } else if (result.outcome === "already_sent") toast.info(text)
    else if (result.outcome === "in_progress") {
      toast.info(text)
      follow(result.message.id)
    } else announce(result.message)
  }, [action, announce, confirmed, follow, recipient, t, target.id, toast])

  const dialog = (
    <Modal
      open={action !== null}
      title={action ? t(`commHub.messages.actions.${action}.title`) : null}
      onCancel={close}
      maskClosable={!submitting}
      closable={!submitting}
      destroyOnHidden
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button onClick={close} disabled={submitting}>
            {t("commHub.wizard.cancel")}
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!preview || !canConfirm(preview, confirmed)}
            onClick={() => void confirm()}
          >
            {action ? t(`commHub.messages.actions.${action}.confirm`) : null}
          </Button>
        </div>
      }
    >
      {loadFailed ? (
        <Alert
          type="error"
          showIcon
          message={t("commHub.messages.actions.previewFailed")}
          action={
            <Button
              size="small"
              onClick={() => action && void loadPreview(action)}
            >
              {t("commHub.common.retry")}
            </Button>
          }
        />
      ) : !preview ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <div className="flex flex-col gap-4">
          {preview.state && (
            <Alert
              type="info"
              showIcon
              message={t(`commHub.messages.actions.outcome.${preview.state}`, {
                defaultValue: preview.state,
                name: recipient
              })}
            />
          )}
          {refusal && refusal !== "blocked" && (
            <Alert
              type="warning"
              showIcon
              message={t(`commHub.messages.actions.refused.${refusal}`)}
            />
          )}
          {failed && (
            <Alert
              type="error"
              showIcon
              message={t("commHub.messages.actions.requestFailed")}
            />
          )}
          {preview.action === "send_now" ? (
            <SendNowBody
              preview={preview}
              target={target}
              confirmed={confirmed}
              onConfirmedChange={setConfirmed}
            />
          ) : (
            <RetryBody
              preview={preview}
              target={target}
              confirmed={confirmed}
              onConfirmedChange={setConfirmed}
            />
          )}
        </div>
      )}
    </Modal>
  )

  return { start, busy: submitting, dialog, actions: offered(target) }
}

/**
 * Send now on a waiting message and Retry on a failed one, with the
 * confirmation each needs. Renders nothing when neither is offered.
 * Shared by the Messages list, its reading pane and a record's Communication tab.
 */
export const QueueActions = ({
  message,
  onDone,
  size = "small",
  className = ""
}: {
  message: QueueActionTarget
  onDone?: (message: MessageDetail) => void
  size?: "small" | "middle"
  className?: string
}) => {
  const { t } = useTranslation()
  const { start, busy, dialog, actions } = useQueueAction({
    target: message,
    onDone
  })
  if (actions.length === 0) return null
  return (
    <span className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      {actions.includes("send_now") && (
        <Button
          size={size}
          icon={<SendOutlined aria-hidden />}
          loading={busy}
          onClick={(event) => {
            event.stopPropagation()
            start("send_now")
          }}
        >
          {t("commHub.messages.actions.send_now.button")}
        </Button>
      )}
      {actions.includes("retry") && (
        <Button
          size={size}
          icon={<RedoOutlined aria-hidden />}
          loading={busy}
          onClick={(event) => {
            event.stopPropagation()
            start("retry")
          }}
        >
          {t("commHub.messages.actions.retry.button")}
        </Button>
      )}
      {dialog}
    </span>
  )
}

export default QueueActions

/** Room a row button leaves at its foot for `QueueRowActions`; "" when the row offers no action. */
export const queueRowPadding = (row: QueueActionTarget) =>
  offered(row).length > 0 ? "pb-12" : ""

/**
 * A list row's Send now / Retry, laid over the foot of the row beside (never
 * inside) the row's own button. The row's `<li>` must be `relative` and its
 * button padded with `queueRowPadding(row)`.
 */
export const QueueRowActions = ({
  row,
  onDone
}: {
  row: QueueActionTarget
  onDone?: (message: MessageDetail) => void
}) =>
  offered(row).length > 0 ? (
    <QueueActions
      message={row}
      onDone={onDone}
      className="absolute bottom-3 right-4"
    />
  ) : null
